import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { getSocialPlatformList } from "../../api/postgre/socialPlatformApi";
import Layout from "../../components/Layout/Layout";
import AccountHeader from "../../components/MyAccount/AccountHeader";
import ProfilePhotoCard from "../../components/MyAccount/ProfilePhotoCard";
import PersonalInfoSection from "../../components/MyAccount/PersonalInfoSection";
import AddressSection from "../../components/MyAccount/AddressSection";
import SocialMediaSection from "../../components/MyAccount/SocialMediaSection";
import AccountSocialModal from "../../components/MyAccount/AccountSocialModal";
import LoadingState from "../../components/Common/LoadingState";
import {
    getProfile,
    updateProfile,
} from "../../api/userApi";
import { deleteMedia } from "../../api/contentApi";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { showFirstFormErrorToast } from "../../utils/formErrorToast";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { AuthContext } from "../../context";

const emptyValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    number: "",
    city: "",
    postalCode: "",
    area: "",
    country: "",
    age: null,
    categories: [],
    comment: "",
};

const mapProfileToForm = (profile) => ({
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    email: profile?.email || "",
    phone: profile?.phone || "",
    street: profile?.street || "",
    number: profile?.number || "",
    city: profile?.city || "",
    postalCode: profile?.postalCode || "",
    area: profile?.area || profile?.location || "",
    country: profile?.countryId ? String(profile.countryId) : "",
    age: profile?.age ? { value: String(profile.age) } : null,
    categories: Array.isArray(profile?.categories)
        ? profile.categories.map((c) => typeof c === "string" ? { value: c } : c)
        : [],
    comment: profile?.comment || "",
});

const normalizeName = (value) => String(value || "").trim();

const Account = () => {
    const { t } = useTranslation();
    const { setAuthUser } = useContext(AuthContext);
    const [isEditing, setIsEditing] = useState(false);
    const [profile, setProfile] = useState(null);
    const [mediaAccounts, setMediaAccounts] = useState([]);
    const [uploadedPhoto, setUploadedPhoto] = useState(null);
    const [isPhotoUploading, setIsPhotoUploading] = useState(false);
    const [activePlatformKey, setActivePlatformKey] = useState(null);
    const [platforms, setPlatforms] = useState([]);
    const [handles, setHandles] = useState({});
    const [socialLinks, setSocialLinks] = useState({});
    const loadAction = useAsyncAction();
    const { run: runLoadProfile } = loadAction;
    const saveAction = useAsyncAction();
    const { run: runSaveProfile } = saveAction;
    const isLoading = loadAction.isLoading;
    const isSaving = saveAction.isLoading;
    const apiError = saveAction.errorMessage || loadAction.errorMessage;

    useEffect(() => {
        getSocialPlatformList()
            .then((list) => {
                setPlatforms(list);
                setHandles(Object.fromEntries(list.map((p) => [p.key, ""])));
                setSocialLinks(Object.fromEntries(list.map((p) => [p.key, ""])));
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!apiError) return;
        showErrorToast(apiError);
    }, [apiError]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: emptyValues,
    });

    const loadProfile = useCallback(async () => {
        const result = await runLoadProfile(
            () => getProfile(),
            { errorFallback: t("ACCOUNT_ERROR_LOAD_PROFILE"), silentErrorToast: true }
        );
        if (!result) {
            setProfile(null);
            setMediaAccounts([]);
            reset(emptyValues);
            return;
        }
        const profileData = result;
        const safeAccounts = profileData?.socialMediaAccounts || [];
        setProfile(profileData || null);
        setMediaAccounts(safeAccounts);
        reset(mapProfileToForm(profileData || {}));
        const initHandles = Object.fromEntries(
            platforms.map((p) => {
                const acc = safeAccounts.find((a) => (a.code || a.platform || "").toLowerCase() === p.key);
                return [p.key, acc?.handle || ""];
            })
        );
        const initLinks = Object.fromEntries(
            platforms.map((p) => {
                const acc = safeAccounts.find((a) => (a.code || a.platform || "").toLowerCase() === p.key);
                return [p.key, acc?.profileUrl || ""];
            })
        );
        setHandles(initHandles);
        setSocialLinks(initLinks);
        if (profileData) setAuthUser(profileData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reset, runLoadProfile, t]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    const initialFormValues = useMemo(
        () => mapProfileToForm(profile || {}),
        [profile]
    );
    const verificationByPlatform = useMemo(
        () => Object.fromEntries(
            platforms.map((platform) => {
                const account = mediaAccounts.find((item) => (item.code || item.platform || "").toLowerCase() === platform.key);
                const currentHandle = (handles[platform.key] || "").trim();

                if (!currentHandle) {
                    return [platform.key, "unverified"];
                }

                if (account?.handle === currentHandle && account?.verificationStatus) {
                    return [platform.key, account.verificationStatus];
                }

                return [platform.key, "unverified"];
            })
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [handles, mediaAccounts]
    );

    const cleanupUploadedPhoto = useCallback(async () => {
        if (!uploadedPhoto?.cloudflareId) return;
        try {
            await deleteMedia({ cloudflareId: uploadedPhoto.cloudflareId, type: "image" });
        } catch {}
        setUploadedPhoto(null);
    }, [uploadedPhoto]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleCancel = async () => {
        await cleanupUploadedPhoto();
        setIsEditing(false);
        reset(initialFormValues);
        const resetHandles = Object.fromEntries(
            platforms.map((p) => {
                const acc = mediaAccounts.find((a) => (a.code || a.platform || "").toLowerCase() === p.key);
                return [p.key, acc?.handle || profile?.[p.key] || ""];
            })
        );
        const resetLinks = Object.fromEntries(
            platforms.map((p) => {
                const acc = mediaAccounts.find((a) => (a.code || a.platform || "").toLowerCase() === p.key);
                return [p.key, acc?.profileUrl || profile?.[p.key + "Url"] || ""];
            })
        );
        setHandles(resetHandles);
        setSocialLinks(resetLinks);
    };

    const handleSocialSave = (key, handle, link) => {
        setHandles((prev) => ({ ...prev, [key]: handle }));
        setSocialLinks((prev) => ({ ...prev, [key]: link }));
    };



    const handleSave = async (values) => {
        const result = await runSaveProfile(
            () => updateProfile({
                firstName: normalizeName(values.firstName),
                lastName: normalizeName(values.lastName),
                email: values.email,
                phone: values.phone,
                street: values.street,
                number: values.number,
                city: values.city,
                postalCode: values.postalCode,
                area: values.area || "",
                countryId: values.country ? Number(values.country) : null,
                age: values.age?.value || "",
                categories: (() => {
                    const cats = values.categories || [];
                    if (cats.some((c) => c.value === "all")) return ["all"];
                    return cats.map((c) => c.value);
                })(),
                comment: values.comment || "",
                profilePhotoUrl: uploadedPhoto?.mediaUrl,
                socialMediaJson: platforms
                    .filter((p) => (handles[p.key] || "").trim())
                    .map((p) => ({
                        SocialMediaName: p.name,
                        SocialMediaCode: p.code || p.name,
                        SocialMediaAccountName: (handles[p.key] || "").trim(),
                        SocialMediaUrl: (socialLinks[p.key] || "").trim(),
                    })),
            }),
            { errorFallback: t("ACCOUNT_ERROR_UPDATE_PROFILE"), silentErrorToast: true }
        );

        if (!result) return;

        if (!result?.success) {
            showErrorToast(result?.message || t("ACCOUNT_ERROR_UPDATE_PROFILE"));
            return;
        }

        setIsEditing(false);
        showSuccessToast(result?.message || t("ACCOUNT_SUCCESS_PROFILE_UPDATED"));

        setUploadedPhoto(null);
        await loadProfile();
    };

    const handleFormInvalid = (formErrors) => {
        showFirstFormErrorToast(formErrors);
    };

    return (
        <Layout activeLabel="Account">
            {isLoading ? (
                <LoadingState message={t("ACCOUNT_LOADING_PROFILE")} fullScreen />
            ) : (
                <form onSubmit={handleSubmit(handleSave, handleFormInvalid)}>
                    <AccountHeader
                        isEditing={isEditing}
                        onEdit={handleEdit}
                        onSave={handleSubmit(handleSave, handleFormInvalid)}
                        onCancel={handleCancel}
                        isValid={isValid && !isSaving && !isPhotoUploading}
                    />

                    <div className="mt-8 grid gap-8 lg:grid-cols-[240px_1fr]">
                        <ProfilePhotoCard
                            imageUrl={uploadedPhoto?.mediaUrl || profile?.profilePhotoUrl || null}
                            isEditing={isEditing}
                            onUploaded={setUploadedPhoto}
                            onUploadError={(message) => showErrorToast(message)}
                            onUploadingChange={setIsPhotoUploading}
                        />

                        <div className="space-y-8">
                            <PersonalInfoSection
                                control={control}
                                errors={errors}
                                isEditing={isEditing}
                            />
                            <AddressSection
                                control={control}
                                errors={errors}
                                isEditing={isEditing}
                            />
                            <div>
                                <p className="text-sm font-semibold text-slate-900">{t("ACCOUNT_COMMENT")}</p>
                                <div className="mt-4">
                                    <Controller
                                        name="comment"
                                        control={control}
                                        render={({ field }) => (
                                            isEditing ? (
                                                <textarea
                                                    {...field}
                                                    rows={4}
                                                    placeholder={t("ACCOUNT_PLACEHOLDER_COMMENT")}
                                                    className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm outline-none focus:outline-none transition focus:border-primary resize-none"
                                                />
                                            ) : (
                                                <div className="w-full rounded-xl border border-slate-200 bg-input-bg px-4 py-3 text-sm text-slate-400 min-h-[48px] whitespace-pre-wrap">
                                                    {field.value || <span className="text-slate-400">—</span>}
                                                </div>
                                            )
                                        )}
                                    />
                                </div>
                            </div>
                            <SocialMediaSection
                                handles={handles}
                                links={socialLinks}
                                verificationByPlatform={verificationByPlatform}
                                isEditing={isEditing}
                                platforms={platforms}
                                onOpenModal={(key) => setActivePlatformKey(key)}
                            />
                        </div>
                    </div>
                </form>
            )}

            <AccountSocialModal
                isOpen={Boolean(activePlatformKey)}
                platform={platforms.find((p) => p.key === activePlatformKey) || null}
                initialHandle={handles[activePlatformKey] || ""}
                initialLink={socialLinks[activePlatformKey] || ""}
                onSave={handleSocialSave}
                onClose={() => setActivePlatformKey(null)}
            />
        </Layout>
    );
};

export default Account;
